
import { StatItem, RecentOrder, FactoryPerformanceData, RawOrderData } from "./types";

export const validateOrderStatus = (status: string): "pending" | "in-progress" | "completed" | "canceled" => {
  const validStatuses = ["pending", "in-progress", "completed", "canceled"];
  return validStatuses.includes(status) ? status as any : "pending";
};

export const formatOrders = (orders: RawOrderData[], factories: any[]): RecentOrder[] => {
  const factoryMap = new Map(factories.map(f => [f.id, f.name]));
  
  return orders.slice(0, 10).map(order => ({
   
    id: order.id,
    customer: order.customers?.customer_name || " ",
    date: new Date(order.created_at).toLocaleDateString(),
    status: validateOrderStatus(order.status),
    amount: `$${Number(order.total_amount || 0).toFixed(2)}`,
    factory: factoryMap.get(order.factory_id) || "Unknown Factory"
  }));
};

export const calculateFactoryPerformance = (factories: any[], orders: RawOrderData[]): FactoryPerformanceData[] => {
  return factories.map(factory => {
    const factoryOrders = orders.filter(order => order.factory_id === factory.id);
    const completedOrders = factoryOrders.filter(order => order.status === "completed").length;
    const pendingOrders = factoryOrders.filter(order => order.status === "pending").length;
    const totalOrders = factoryOrders.length;
    
    return {
      name: factory.name,
      completedOrders,
      pendingOrders,
      efficiency: totalOrders > 0 ? Math.round((completedOrders / totalOrders) * 100) : 0
    };
  });
};

export const calculateStats = (
  totalOrders: number,
  activeFactories: number,
  newCustomers: number,
  orderRevenue: number,
  prevMonthOrderRevenue: number,
  lastMonthOrdersCount: number,
  subscriptionRevenue: number,
  prevMonthSubscriptionRevenue: number
): StatItem[] => {
  console.log("=== CALCULATING DASHBOARD STATS ===");
  console.log("Parameters:");
  console.log("- totalOrders:", totalOrders);
  console.log("- activeFactories:", activeFactories);
  console.log("- newCustomers:", newCustomers);
  console.log("- orderRevenue:", orderRevenue);
  console.log("- subscriptionRevenue:", subscriptionRevenue);
  
  // Calculate total revenue (order revenue + subscription revenue)
  const totalRevenue = orderRevenue + subscriptionRevenue;
  const prevTotalRevenue = prevMonthOrderRevenue + prevMonthSubscriptionRevenue;
  
  // Calculate revenue change
  const revenueChange = prevTotalRevenue > 0 
    ? ((totalRevenue - prevTotalRevenue) / prevTotalRevenue) * 100
    : totalRevenue > 0 ? 100 : 0;
  
  // Calculate order change
  const orderChange = lastMonthOrdersCount > 0 
    ? ((totalOrders - lastMonthOrdersCount) / lastMonthOrdersCount) * 100
    : totalOrders > 0 ? 100 : 0;

  const stats: StatItem[] = [
    {
      title: "Total Orders",
      value: totalOrders.toString(),
      icon: "shopping-cart-2-line",
      change: `${orderChange >= 0 ? '+' : ''}${orderChange.toFixed(1)}% from last month`,
      trend: orderChange >= 0 ? "up" : "down",
    },
    {
      title: "Active Factories",
      value: activeFactories.toString(),
      icon: "building-2-line",
      change: "Currently active",
      trend: "neutral",
    },
    {
      title: "New Customers",
      value: newCustomers.toString(),
      icon: "user-line",
      change: "This month",
      trend: newCustomers > 0 ? "up" : "neutral",
    },
    {
      title: "Total Revenue",
      value: `$${totalRevenue.toFixed(2)}`,
      icon: "money-dollar-circle-line",
      change: `${revenueChange >= 0 ? '+' : ''}${revenueChange.toFixed(1)}% from last month`,
      trend: revenueChange >= 0 ? "up" : "down",
    }
  ];

  // Add subscription-specific stat if there's subscription revenue
  if (subscriptionRevenue > 0) {
    stats.push({
      title: "Subscription Revenue",
      value: `$${subscriptionRevenue.toFixed(2)}`,
      icon: "repeat-line",
      change: "Monthly recurring revenue",
      trend: "up",
    });
  }

  console.log("=== FINAL CALCULATED STATS ===");
  stats.forEach(stat => {
    console.log(`${stat.title}: ${stat.value} (${stat.change})`);
  });

  return stats;
};
